(() => {
  'use strict';

  const body = document.body;
  const cartType = body.dataset.cartType || 'drawer';

  const qs = (s, ctx) => (ctx || document).querySelector(s);
  const qsa = (s, ctx) => Array.from((ctx || document).querySelectorAll(s));

  /* ---------------- Money formatting (fallback if Shopify.currency missing) ---------------- */
  function money(cents) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ---------------- Live region for a11y announcements ---------------- */
  function announce(msg) {
    const region = qs('#CartLiveRegion');
    if (region) region.textContent = msg;
  }

  /* ---------------- Cart drawer open/close ---------------- */
  let lastFocused = null;

  function openCart() {
    if (cartType === 'page') {
      window.location.href = '/cart';
      return;
    }
    lastFocused = document.activeElement;
    body.classList.add('cart-open');
    const drawer = qs('[data-cart-drawer]');
    if (drawer) {
      const closeBtn = qs('[data-cart-close]', drawer);
      if (closeBtn) closeBtn.focus();
    }
    document.addEventListener('keydown', onCartKeydown);
  }

  function closeCart() {
    body.classList.remove('cart-open');
    document.removeEventListener('keydown', onCartKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function onCartKeydown(e) {
    if (e.key === 'Escape') closeCart();
  }

  /* ---------------- Search drawer ---------------- */
  function openSearch() {
    body.classList.add('search-open');
    const input = qs('#SearchInput');
    if (input) setTimeout(() => input.focus(), 50);
    document.addEventListener('keydown', onSearchKeydown);
  }
  function closeSearch() {
    body.classList.remove('search-open');
    document.removeEventListener('keydown', onSearchKeydown);
  }
  function onSearchKeydown(e) {
    if (e.key === 'Escape') closeSearch();
  }

  /* ---------------- Mobile menu ---------------- */
  function toggleMobileMenu() {
    const nav = qs('[data-mobile-nav]');
    const toggle = qs('[data-mobile-menu-toggle]');
    if (!nav) return;
    const isOpen = nav.classList.toggle('open');
    nav.setAttribute('aria-hidden', String(!isOpen));
    if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
  }

  /* ---------------- Global click delegation ---------------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openCart(); }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeCart(); }
    if (e.target.closest('[data-search-open]')) { e.preventDefault(); openSearch(); }
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); closeSearch(); }
    if (e.target.closest('[data-mobile-menu-toggle]')) toggleMobileMenu();

    // Quantity steppers (product page + cart page) — plain input increment, no fetch needed
    const stepBtn = e.target.closest('[data-qty-increase],[data-qty-decrease]');
    if (stepBtn) {
      const wrap = stepBtn.closest('.qty-stepper');
      const input = wrap && wrap.querySelector('input[type="number"]');
      if (input) {
        const min = Number(input.min) || 0;
        let val = Number(input.value) || 0;
        val += stepBtn.hasAttribute('data-qty-increase') ? 1 : -1;
        if (val < min) val = min;
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  /* ---------------- AJAX add-to-cart (progressive enhancement) ---------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-add-form]');
    if (!form) return;
    e.preventDefault();

    const button = form.querySelector('[data-add-button], button[type="submit"]');
    const errorEl = form.querySelector('.form-error');
    if (errorEl) errorEl.remove();
    if (button) { button.disabled = true; button.classList.add('is-loading'); }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body: new URLSearchParams(new FormData(form)),
      });
      const data = await res.json();

      if (!res.ok) {
        const message = (data && (data.description || data.message)) || 'This item could not be added to your bag.';
        showFormError(form, message);
        announce(message);
        return;
      }

      await refreshCart();
      announce('Added to your bag.');
      if (cartType === 'page') {
        window.location.href = '/cart';
      } else {
        openCart();
      }
    } catch (err) {
      showFormError(form, 'Something went wrong adding this item. Please try again.');
    } finally {
      if (button) { button.disabled = false; button.classList.remove('is-loading'); }
    }
  });

  function showFormError(form, message) {
    let el = form.querySelector('.form-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'form-error';
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.textContent = message;
  }

  /* ---------------- Refresh cart drawer + counts ---------------- */
  async function refreshCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await res.json();

    qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });

    const box = qs('[data-cart-items]');
    const totalEl = qs('[data-cart-total]');
    if (!box) return cart;

    if (cart.item_count === 0) {
      const emptyText = escapeHtml(body.dataset.cartEmptyText || 'Your bag is empty.');
      const browseText = escapeHtml(body.dataset.cartBrowseText || 'Browse releases →');
      box.innerHTML = `<div class="cart-empty"><p>${emptyText}</p><a href="/#shop" data-cart-close>${browseText}</a></div>`;
      if (totalEl) totalEl.textContent = money(0);
      return cart;
    }

    box.innerHTML = cart.items.map((item) => `
      <div class="cart-line" data-cart-line="${item.key}">
        <img src="${item.image}" alt="${escapeHtml(item.product_title)}" width="90" height="90" loading="lazy">
        <div>
          <b>${escapeHtml(item.product_title)}</b>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="cart-line-variant">${escapeHtml(item.variant_title)}</span>` : ''}
          <span>${money(item.final_line_price)}</span>
          <div class="qty">
            <button data-qty="${item.key}" data-change="-1" aria-label="Decrease quantity">−</button>
            <em>${item.quantity}</em>
            <button data-qty="${item.key}" data-change="1" aria-label="Increase quantity">+</button>
            <button data-remove="${item.key}">Remove</button>
          </div>
        </div>
      </div>
    `).join('');

    if (totalEl) totalEl.textContent = money(cart.total_price);
    return cart;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  /* ---------------- Cart drawer quantity change / remove ---------------- */
  let cartUpdateInFlight = false;

  document.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-qty]');
    const removeBtn = e.target.closest('[data-remove]');
    if (!qtyBtn && !removeBtn) return;
    if (cartUpdateInFlight) return;

    const key = (qtyBtn || removeBtn).dataset.qty || (qtyBtn || removeBtn).dataset.remove;
    let quantity = 0;

    if (qtyBtn) {
      const line = qtyBtn.closest('.cart-line');
      const current = Number(line.querySelector('em').textContent) || 0;
      quantity = Math.max(0, current + Number(qtyBtn.dataset.change));
    }

    cartUpdateInFlight = true;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        announce(data.description || 'Could not update your bag.');
      }
      await refreshCart();
    } catch (err) {
      announce('Could not update your bag. Please check your connection and try again.');
    } finally {
      cartUpdateInFlight = false;
    }
  });

  /* ---------------- Product page: variant selection sync ---------------- */
  function initProductForm(form) {
    const variantScript = form.querySelector('[data-product-variants]');
    if (!variantScript) return;

    let variants;
    try { variants = JSON.parse(variantScript.textContent); } catch (e) { return; }

    const optionSelects = qsa('.option-select', form);
    const hiddenId = form.querySelector('[data-variant-id]');
    const nativeSelect = form.querySelector('[data-variant-select]');
    const priceEl = qs('[data-product-price]');
    const addButton = form.querySelector('[data-add-button]');
    const buttonText = addButton ? addButton.querySelector('[data-button-text]') : null;

    function findVariant() {
      if (optionSelects.length === 0) return variants[0];
      const chosen = optionSelects.map((s) => s.value);
      return variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3].filter((o) => o !== null && o !== undefined);
        return chosen.every((val, i) => opts[i] === val);
      });
    }

    function updateForVariant(variant) {
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (buttonText) buttonText.textContent = 'UNAVAILABLE';
        return;
      }
      if (hiddenId) hiddenId.value = variant.id;
      if (nativeSelect) nativeSelect.value = variant.id;
      if (priceEl) {
        const amountEl = priceEl.querySelector('[data-price-amount]');
        if (amountEl) amountEl.textContent = money(variant.price);
      }
      if (addButton) addButton.disabled = !variant.available;
      if (buttonText) {
        buttonText.textContent = variant.available
          ? `ADD TO BAG — ${money(variant.price)}`
          : 'SOLD OUT';
      }
    }

    optionSelects.forEach((select) => {
      select.addEventListener('change', () => updateForVariant(findVariant()));
    });
  }

  qsa('[data-add-form]').forEach((form) => {
    if (form.querySelector('[data-product-variants]')) initProductForm(form);
  });

  /* ---------------- Product gallery thumbnails ---------------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const index = thumb.dataset.galleryThumb;
    const gallery = thumb.closest('.product-gallery');
    if (!gallery) return;
    qsa('[data-gallery-image]', gallery).forEach((img) => img.classList.toggle('is-active', img.dataset.galleryImage === index));
    qsa('[data-gallery-thumb]', gallery).forEach((t) => t.classList.toggle('is-active', t === thumb));
  });

  /* ---------------- Collection sort auto-submit ---------------- */
  const sortSelect = qs('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const form = sortSelect.closest('form');
      if (form) form.submit();
    });
  }

  /* ---------------- Scroll reveal ---------------- */
  const animationsEnabled = body.dataset.scrollAnimations !== 'false';
  const revealEls = qsa('[data-reveal]');
  if (revealEls.length && animationsEnabled && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------------- Countdown timers ---------------- */
  qsa('[data-countdown]').forEach((el) => {
    const raw = el.dataset.countdown;
    if (!raw) return;
    const target = new Date(raw.replace(' ', 'T')).getTime();
    if (isNaN(target)) return;

    const daysEl = qs('[data-cd-days]', el);
    const hoursEl = qs('[data-cd-hours]', el);
    const minutesEl = qs('[data-cd-minutes]', el);
    const secondsEl = qs('[data-cd-seconds]', el);

    function tick() {
      const diff = target - Date.now();
      if (diff <= 0) {
        el.innerHTML = `<div class="countdown-expired">${el.dataset.expiredText || "IT'S LIVE."}</div>`;
        clearInterval(timer);
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
      if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
    }

    tick();
    const timer = setInterval(tick, 1000);
  });
})();
